/**
 * ProjectIntegrationsPage Component
 *
 * Project-level integrations page accessible from the sidebar.
 * Composes existing MCPServerList and GoDaddyCredentials components
 * within a project context.
 *
 * Route: /projects/:projectId/integrations
 * Ticket: AC-1111
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plug } from 'lucide-react';
import MCPServerList from '../settings/MCPServerList';
import GoDaddyCredentials from '../settings/GoDaddyCredentials';
import { useProjectStore } from '../../store';

const ProjectIntegrationsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { currentProject, projects, loadProjects, setCurrentOperation } = useProjectStore();
  const numericProjectId = projectId ? parseInt(projectId, 10) : null;

  useEffect(() => {
    const ensureCurrentOperation = async () => {
      if (!projectId) return;
      if (!currentProject || String(currentProject.project_id) !== projectId) {
        try {
          if (projects.length === 0) {
            await loadProjects();
          }
          const project = useProjectStore.getState().projects.find(p => String(p.project_id) === projectId);
          if (project) {
            setCurrentOperation(project);
          }
        } catch (err) {
          console.error('Error loading project:', err);
        }
      }
    };
    ensureCurrentOperation();
  }, [projectId, currentProject, projects, loadProjects, setCurrentOperation]);

  if (!numericProjectId) {
    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        Invalid project ID.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-3 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Project
        </button>
        <div className="flex items-center gap-3">
          <Plug className="w-6 h-6 text-primary-600" />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">
              Integrations
            </h1>
            {currentProject && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Manage integrations for <span className="font-medium">{currentProject.name}</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* MCP Servers */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6 mb-6">
        <MCPServerList projectId={numericProjectId} />
      </div>

      {/* GoDaddy Credentials */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6">
        <GoDaddyCredentials projectId={numericProjectId} />
      </div>
    </div>
  );
};

export default ProjectIntegrationsPage;
